/*
 * Shared pieces of the audit event export call. Four clients download events — audit
 * and security, each at the organization level (FPAPI) and the project level (CC) —
 * and all four send the same request shape and read the same response headers, so the
 * common query, the request options, the filename, the result and the error decoding
 * live here instead of being written four times.
 */

export const EXPORT_URL = '/audit/v1/eventExport'

/*
 * The request options every export sends, whatever the kind or the level.
 *
 * No X-Pagination-Size: asking for no size returns this deployment's export maximum,
 * and a size outside its bounds is rejected rather than reduced. The blob response type
 * keeps the file from being parsed as text, and an export builds the whole file before
 * answering, so it needs more room than the 60s default the list routes run with.
 * No retries: a timed-out export would otherwise run again up to 3 times.
 */
export const exportRequestOptions = {
  method: 'get',
  url: EXPORT_URL,
  responseType: 'blob',
  timeout: 120 * 1000,
  retries: 0,
  originalResponse: true
}

// Matches both content-disposition spellings: a quoted `filename=` and the
// RFC 5987 `filename*=UTF-8''` form used when the name is not plain ASCII.
const FILENAME_PATTERN = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i

/*
 * The name a file takes when the API sends no content-disposition, keyed by event kind.
 * The two kinds are named apart so exporting both tabs in one sitting does not save the
 * same name twice and leave the browser to number the second one. `AUDIT` is the default
 * because the route serves the whole audit trail when no kind narrows it.
 */
const FALLBACK_FILENAME = {
  AUDIT: 'audit-events',
  SECURITY: 'security-events'
}

/**
 * Builds the part of the export query that every kind and level sends. Each client adds
 * its own `eventKind` and its own filters on top, named as its list route names them, so
 * the file holds what the table is showing.
 *
 * @param {Object} params
 * @param {string} [params.format='JSON'] - File format: `JSON`, `CSV`, `XML` or `XLSX`. Case-insensitive.
 * @param {string} [params.search]        - Free-text query, sent as `q`.
 * @param {string|Date} [params.from]     - Start of the time range (converted to RFC 3339).
 * @param {string|Date} [params.to]       - End of the time range (converted to RFC 3339).
 * @returns {Object} Axios query parameters.
 */
export function exportBaseParams (params = {}) {
  return {
    // The enum is spelled in upper case and is case-sensitive
    format: (params.format || 'JSON').toUpperCase(),
    q: params.search?.trim() || undefined, // Full-text search. Trimmed so a blank box sends nothing at all.
    from: params.from ? new Date(params.from).toISOString() : undefined, // RFC 3339 format, e.g. 2025-01-01T00:00:00Z
    to: params.to ? new Date(params.to).toISOString() : undefined // RFC 3339 format, e.g. 2025-01-01T00:00:00Z
  }
}

/**
 * Reads the attachment name the API chose. Falls back to a name built from the event kind
 * and the requested format so the download is never left unnamed.
 *
 * @param {Object} headers - Response headers.
 * @param {string} [format] - The requested format, used for the fallback extension.
 * @param {string} [kind='AUDIT'] - The event kind that was exported, `AUDIT` or `SECURITY`.
 * @returns {string}
 */
export function exportFilename (headers, format, kind = 'AUDIT') {
  const disposition = headers['content-disposition'] || headers['Content-Disposition'] || ''
  const match = FILENAME_PATTERN.exec(disposition)
  if (match && match[1]) return decodeURIComponent(match[1])
  return `${FALLBACK_FILENAME[kind]}.${(format || 'json').toLowerCase()}`
}

/**
 * Turns an export response into what the download helper needs: the file itself, plus
 * what the API reported about it in the headers.
 *
 * @param {Object} response - The Axios response, kept whole via `originalResponse`.
 * @param {string} [format] - The requested format, used for the fallback filename.
 * @param {string} [kind]   - The event kind that was exported, used for the fallback filename.
 * @returns {{blob: Blob, filename: string, total: number|null, hasMore: boolean, kinds: Array}}
 */
export function exportResult (response, format, kind) {
  const headers = response?.headers || {}
  const total = parseInt(headers['x-pagination-elements'])
  return {
    blob: response?.data,
    filename: exportFilename(headers, format, kind),
    total: isNaN(total) ? null : total,
    // A cursor means this file holds only the first page of the matching events
    hasMore: !!headers['x-pagination-cursor-next'],
    kinds: headers['x-export-event-kinds']?.split(',').map(k => k.trim()).filter(k => k) || []
  }
}

/**
 * Picks the export out of a fan-out that settles one call per dataplant.
 *
 * An export is a single file, so the first dataplant to answer is the answer. Keeping
 * only the fulfilled calls would lose the API's message on a failure, so the rejection
 * is read back and thrown instead.
 *
 * @param {Array} settled - Settled results, as returned with `{ fullResponse: true }`.
 * @returns {Promise<Object>} The fulfilled Axios response.
 * @throws {Error} The rejection carried by the fan-out, with a message worth showing.
 */
export async function firstFulfilledExport (settled) {
  const response = settled?.find(r => r.status === 'fulfilled')?.value
  if (response) return response

  const rejection = settled?.find(r => r.status === 'rejected')?.reason
  // No rejection either means the fan-out found no dataplant to ask
  if (!rejection) throw new Error('auditing.download.error.no_response')
  throw await readBlobError(rejection)
}

/**
 * Puts a readable message back on a failed export.
 *
 * Asking for a blob means the API's error payload arrives as a blob as well, so the
 * usual message extraction in API.request finds nothing and the user would be shown
 * an empty error. Read the payload and use the message it carries.
 *
 * @param {Error} err - The error thrown by API.request.
 * @returns {Promise<Error>} The same error, with a message worth showing.
 */
export async function readBlobError (err) {
  const data = err?.response?.data
  if (typeof data?.text !== 'function') return err
  try {
    const body = JSON.parse(await data.text())
    const message = body.message || body.error?.message || body.error || body.description
    if (message) {
      err.message = message
      // The toaster reads response.data before err.message, so the blob must not stay there.
      // The rest of the body stays readable (AppService puts `class` and `details` next to it).
      err.response.data = { ...body, message }
    }
  } catch (parseError) {
    // Not a JSON payload (a gateway HTML page, say): the original message is the best we have
  }
  return err
}
